import React from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage, useIntl } from 'react-intl'
import { EMPTY_VALUE, TONE } from '../../data/constants'
import { formatRelativeDate } from '../../data/reportCopy'
import Chip from '../../components/Chip/Chip'
import EmptyState from '../../components/EmptyState/EmptyState'
import ReportTable from '../../components/ReportTable/ReportTable'
import SubBlock from '../../components/SubBlock/SubBlock'
import styles from './DataExposureSection.module.scss'

const STALE_WARN_DAYS = 30
const STALE_DANGER_DAYS = 90
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000
const PENDING_GUEST_STATE = 'pendingacceptance'

const getStartOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate())

const getStalenessTone = (value) => {
  if (!value) return null

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null

  const daysAgo = Math.max(0, Math.floor((getStartOfDay(new Date()) - getStartOfDay(date)) / MILLISECONDS_PER_DAY))
  if (daysAgo > STALE_DANGER_DAYS) return TONE.DANGER
  if (daysAgo > STALE_WARN_DAYS) return TONE.WARN
  return null
}

const renderStaleDate = (intl, value) => {
  const label = formatRelativeDate(intl, value)
  const tone = getStalenessTone(value)
  if (!tone) return label
  return <span className={`${tone === TONE.DANGER ? 'text-danger' : 'text-warning'} fw-bold`}>{label}</span>
}

const getTableFooter = ({ shown, total }) => {
  if (shown === 0 || total <= shown) return null

  return <FormattedMessage id="AI_RISK_REPORT.DATA.GUESTS.TABLE_FOOTER" values={{ shown, total }} />
}

const formatStatValue = (value) => (value === null || value === undefined ? EMPTY_VALUE : value)

const getPendingTone = (pending) => {
  if (!Number.isFinite(pending)) return undefined
  if (pending > 0) return TONE.WARN
  return TONE.OK
}

export default function GuestAccess({ summary, guests, score, gap = null }) {
  const intl = useIntl()
  const hasGuestData = Number.isFinite(score.value)
  const total = summary.users?.guests?.total
  const usersTotal = summary.users?.total ?? 0
  const guestPct =
    hasGuestData && Number.isFinite(total) && usersTotal > 0 ? Math.round((total / usersTotal) * 100) : EMPTY_VALUE
  const pending = summary.users?.guests?.pending
  const accepted = summary.users?.guests?.accepted
  const guestRows = guests.map((guest) => ({ ...guest, key: guest.entityId }))

  const stats = [
    {
      key: 'total',
      value: hasGuestData ? total : EMPTY_VALUE,
      label: <FormattedMessage id="AI_RISK_REPORT.DATA.GUESTS.STAT_TOTAL" values={{ pct: guestPct }} />,
    },
    {
      key: 'pending',
      value: formatStatValue(pending),
      denom: hasGuestData && total > 0 ? total : undefined,
      tone: getPendingTone(pending),
      label: <FormattedMessage id="AI_RISK_REPORT.DATA.GUESTS.STAT_PENDING" />,
    },
    {
      key: 'accepted',
      value: formatStatValue(accepted),
      denom: hasGuestData && total > 0 ? total : undefined,
      label: <FormattedMessage id="AI_RISK_REPORT.DATA.GUESTS.STAT_ACCEPTED" />,
    },
  ]

  const columns = [
    {
      key: 'guest',
      header: <FormattedMessage id="AI_RISK_REPORT.DATA.GUESTS.COL_GUEST" />,
      render: (row) => (
        <>
          <div className="text-body fw-bold">{row.displayName || row.userPrincipalName}</div>
          <div className={styles.cellSub}>{row.mail || row.companyName}</div>
        </>
      ),
      sortable: true,
      sortValue: (row) => (row.displayName || row.userPrincipalName || '').toLowerCase(),
    },
    {
      key: 'lastSignIn',
      header: <FormattedMessage id="AI_RISK_REPORT.SKELETON.COL_LAST_SIGNIN" />,
      render: (row) => renderStaleDate(intl, row.lastSignInAt),
      sortable: true,
      sortValue: (row) => row.lastSignInAt || '',
    },
    {
      key: 'groups',
      header: <FormattedMessage id="AI_RISK_REPORT.SKELETON.COL_GROUPS" />,
      render: (row) => row.groupMembershipCount ?? '-',
      sortable: true,
      sortValue: (row) => row.groupMembershipCount ?? -1,
    },
    {
      key: 'state',
      header: <FormattedMessage id="AI_RISK_REPORT.COL_ISSUE" />,
      render: (row) =>
        row.externalUserState === PENDING_GUEST_STATE ? (
          <Chip tone={TONE.WARN}>
            <FormattedMessage id="AI_RISK_REPORT.DATA.GUESTS.STATE_PENDING" />
          </Chip>
        ) : (
          '-'
        ),
      sortable: true,
      sortValue: (row) => row.externalUserState || '',
    },
  ]

  return (
    <SubBlock
      titleId="AI_RISK_REPORT.DATA.GUESTS.TITLE"
      subtitleId="AI_RISK_REPORT.DATA.GUESTS.SUBTITLE"
      score={score.value}
      tone={score.tone}
      gap={gap}
      stats={stats}
    >
      <ReportTable
        columns={columns}
        rows={guestRows}
        footer={getTableFooter({ shown: guestRows.length, total })}
        empty={
          <EmptyState
            messageId={hasGuestData ? 'AI_RISK_REPORT.NO_GUESTS' : 'AI_RISK_REPORT.DATA_NOT_ASSESSED'}
            values={{ dataType: 'Guest user' }}
            tone={hasGuestData ? TONE.OK : TONE.NEUTRAL}
          />
        }
      />
    </SubBlock>
  )
}

const summaryPropType = PropTypes.shape({
  users: PropTypes.shape({
    total: PropTypes.number,
    guests: PropTypes.shape({ total: PropTypes.number, pending: PropTypes.number, accepted: PropTypes.number }),
  }),
})

GuestAccess.propTypes = {
  summary: summaryPropType.isRequired,
  guests: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  score: PropTypes.shape({ value: PropTypes.number, tone: PropTypes.string }).isRequired,
  gap: PropTypes.shape({ reason: PropTypes.string, requiredLicense: PropTypes.string }),
}
